import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlCreationOptions, UrlTree } from '@angular/router';
import { Observable, of as observableOf } from 'rxjs';
import { filter, map, switchMap, take, timeout } from 'rxjs/operators';

import { AuthService } from './auth.service';
import { LaunchDarklyService } from './feature-flags/launchdarkly.service';

/**
 * Guards a route based on a feature flag that has individual user targets configured.
 * This guard ensures LaunchDarkly has identified the user before evaluating the flag,
 * preventing the race condition where the flag is evaluated before user identification completes.
 *
 * Can navigate to `redirectTo`, if specified.
 * Can preserve query params, if specified.
 *
 * Per wiki https://onemedical.atlassian.net/wiki/spaces/TKB/pages/472252763/Feature+Flags,
 * use the LaunchDarklyService instead of FeatureFlagsSelectors to access feature flag data.
 *
 * @example
 * {
 *   path: 'my-path',
 *   component: MyComponent,
 *   canActivate: [FeatureFlagWithUserTargetGuard],
 *   data: {
 *     featureFlag: FeatureFlags.MY_FEATURE_FLAG,
 *     redirectTo: '/'
 *     preserveQueryParams: true
 *   }
 * }
 */
@Injectable({
  providedIn: 'root',
})
export class FeatureFlagWithUserTargetGuard implements CanActivate {
  constructor(
    private router: Router,
    private authService: AuthService,
    private launchDarklyService: LaunchDarklyService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    let urlTreeOptions: UrlCreationOptions;

    if (route.data.preserveQueryParams) {
      urlTreeOptions = { queryParams: route.queryParams };
    }

    return this.authService.isLoggedIn$().pipe(
      switchMap(isLoggedIn => {
        if (!isLoggedIn) {
          return observableOf(false);
        }

        const evaluateFlag$ = this.launchDarklyService.featureFlag$(route.data.featureFlag, false).pipe(
          map(flag => {
            if (!flag && route.data.redirectTo) {
              return this.router.createUrlTree([route.data.redirectTo], urlTreeOptions);
            }
            return flag;
          }),
        );

        // Wait for LaunchDarkly to identify the user
        // This prevents race conditions when a feature flag has individual user targets configured.
        // Previously, standard FeatureFlagGuard may evaluate the flag before LaunchDarkly completes user identification,
        // which causes LaunchDarkly to treat the user as anonymous, incorrectly disabling the flag even when the user matches the configured target.
        return this.launchDarklyService.userIdentified$.pipe(
          filter(identified => identified === true),
          take(1),
          timeout({ first: 5000, with: () => evaluateFlag$ }),
          switchMap(() => evaluateFlag$),
        );
      }),
    );
  }
}
